/* eslint-disable complexity */
/* eslint-disable max-depth */

/**
 * @file 任务stroe
 * @author sunyingying01
 */

import {makeObservable, observable, action, flow, flowResult, computed} from 'mobx';
import {isEmpty} from 'lodash';
import {STATUS_OPTIONS} from '@/dicts';
import {fetchJobsList} from '@/api/console';
import {requestManager} from '@/utils/ky-hooks/requestManager';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import {getAutoTerminationState} from '@/utils/job/autoTermination';
import {rootStore} from '../index';
import {JobStore} from './JobStore';

export class JobsListStore {
    /** 任务列表 */
    totalNumber: number = 0;
    /** 项目Id */
    projectId: string = '';
    /** 任务列表 */
    jobsList: JobStore[] = [];
    /** 列表加载状态 */
    loading: boolean = false;
    /** 错误信息 */
    error: string | null = null;
    /** 选中的任务id */
    selectedJobId: string | null = null;
    /** 选中的任务配置详情，没有选中任务，默认初始化任务实例，需要新建任务 */
    selectedJobDetail: JobStore = new JobStore();

    /** 正在操作创建的任务, 更新任务名称 */
    currentJobDetail: JobStore = new JobStore();

    /** 任务自动终止提示弹窗 */
    taskTerminatedModalVisible: boolean = false;

    /** 任务筛选状态 */
    filterStatus: FiltetStatusOptionsType = STATUS_OPTIONS[0];

    /** 任务各个状态数量 */
    countTasksByStatusNumber = {
        ALL: 0,
        DRAFT: 0,
        RUNNING: 0,
        COMPLETED: 0,
        PAUSED: 0,
        CANCELLED: 0
    };

    update = action((params: Partial<JobsListStore>) => {
        Object.assign(this, params);
    });

    constructor(projectId: string = '') {
        this.projectId = projectId;
        makeObservable(this, {
            jobsList: observable,
            loading: observable,
            error: observable,
            selectedJobId: observable,
            selectedJobDetail: observable,
            currentJobDetail: observable,
            taskTerminatedModalVisible: observable,
            filterStatus: observable,
            totalNumber: observable,
            countTasksByStatusNumber: observable,
            setLoading: action.bound,
            setError: action.bound,
            setSelectedJobId: action.bound,
            setSelectedJobDetail: action.bound,
            setTaskTerminatedModalVisible: action.bound,
            fetchList: flow.bound,
            setDetailsUpdate: flow.bound,
            syncSelectedJob: action.bound,
            countTasksByStatus: action.bound,
            getJobListInfo: action.bound,
            urlSelectedJobValid: computed,
            checkExceededlimit: computed,
            hasIterationProgressLessThan100: computed,
            refreshVisibleJobs: computed
        });
    }
    /** 获取任务数量限制 - 动态从 userStore 读取 */
    static get LimitNumber() {
        return rootStore.userStore.maxJobCount;
    }
    /** 地址栏中的 jobId 是否存在于当前任务列表 */
    get urlSelectedJobValid() {
        const selectedJobId = jobIdSyncService.getJobIdFromUrl();
        if (selectedJobId) {
            const jobInfo = this.jobsList.find(item => item.job_id === selectedJobId);
            return !!jobInfo;
        }

        return false;

    }

    /** 任务个数是否超过限制 */
    get checkExceededlimit() {
        return this.totalNumber >= JobsListStore.LimitNumber;
    }

    /** 列表中是否存在迭代进度小于 100% 的任务 */
    get hasIterationProgressLessThan100() {
        return this.jobsList.some(job => {
            if (job.iterationSum <= 0) {
                return false;
            }
            return (job.iteration / job.iterationSum) * 100 < 100;
        });
    }

    /** API Key 刷新弹窗展示的任务（过滤草稿和进度100%） */
    get refreshVisibleJobs() {
        return this.jobsList.filter(job => {
            if (['DRAFT', 'FAILED', 'CANCELLED'].includes(job.status)
                || (job.status === 'COMPLETED' && job.page === 'REPORT')
            ) {
                return false;
            }
            if (job.iterationSum <= 0) {
                return true;
            }
            return ((job.iteration / job.iterationSum) * 100).toFixed(2) !== '100.00';
        });
    }

    setFilterStatus(status: FiltetStatusOptionsType) {
        this.setSelectedJobId(null);
        this.setSelectedJobDetail(new JobStore());
        this.filterStatus = status;
    }

    // 设置加载状态
    setLoading(loading: boolean) {
        this.loading = loading;
    }

    // 设置错误信息
    setError(error: string | null) {
        this.error = error;
    }
    /** 列表中获取相应信息 */
    getJobListInfo(job_id: string) {
        return this.jobsList.find(item => item.job_id === job_id);
    }

    /** 用于列表切换，更新任务详情 */
    * setDetailsUpdate(this: JobsListStore) {
        try {
            const list: JobStore[] = yield flowResult(this.fetchList());

            if (list.length > 0) {
                let jobId = '';

                const urlJobId = jobIdSyncService.getJobIdFromUrl();
                if (urlJobId) {
                    if (this.getJobListInfo(urlJobId)) {
                        jobId = urlJobId;
                    } else {
                        this.setSelectedJobId(null);
                        return;
                    }
                } else if (['/index', '/taskRunning'].includes(window.location.pathname)) {
                    jobId = this.jobsList[0]?.job_id || '';
                    if (jobId) {
                        jobIdSyncService.setJobId(jobId);
                    }
                } else {
                    this.setSelectedJobId(null);
                    return;
                }

                if (jobId) {
                    const jobInfo = this.getJobListInfo(jobId);

                    if (jobInfo) {
                        this.setSelectedJobId(jobId);
                    }
                }
            }
        } catch (error) {
            console.error('[setDetailsUpdate] 错误:', error);
        } finally {
        // 7: 在 finally 中确保清除加载状态
            if (this.selectedJobDetail) {
                this.selectedJobDetail.setDetailsLoading?.(false);
            }
        }
    }

    /**
     * 获取任务列表
     * @function fetchList
     * @memberof JobsListStore
     * @description 获取任务列表数据并更新store状态, 每个任务都是一个jobStore实例
     * @returns {Promise<void>} 异步操作结果
     */
    * fetchList(this: JobsListStore, params?: {job_status: string | ''}) {
        const listParams = params ? params : {job_status: this.filterStatus.status};
        const res: ApiResponse = yield fetchJobsList(listParams);
        const data = res.data || {};
        const jobList = data.list || [];

        // 数据转换：API 响应 -> JobStore 实例
        const jobsData = jobList.length > 0
            ? jobList.map((item: any) => {
                const job = new JobStore(item.job_id);
                job.job_id = item.job_id;
                job.name = item.name;
                job.status = item.status;
                job.stage_status = item.stage_status;
                job.iteration = item.iteration ?? 0;
                job.iterationSum = item.iteration_sum ?? 0;
                job.sourceType = item.source_type || '';
                job.page = item.page || '';
                job.cloudType = item.cloud_type || '';
                const autoTerminationState = getAutoTerminationState(item);
                job.isAutoTerminated = autoTerminationState.isAutoTerminated;
                job.sandboxExists = autoTerminationState.sandboxExists;
                job.consumedCredit = autoTerminationState.consumedCredit;
                job.refundedCredit = autoTerminationState.refundedCredit;
                return job;
            })
            : [];

        // 更新状态
        this.countTasksByStatus(data.count_info || {});
        this.jobsList = jobsData;

        // 同步选中任务
        this.syncSelectedJob();

        return this.jobsList;
    }

    /**
     * 同步选中任务状态
     * @description 统一处理 URL jobId + selectedJobId + selectedJobDetail 的同步逻辑
     */
    syncSelectedJob() {
        // 列表为空：清空所有选中状态
        if (this.jobsList.length === 0) {
            jobIdSyncService.clearJobId();
            this.selectedJobId = null;
            return;
        }

        const urlJobId = jobIdSyncService.getJobIdFromUrl();
        if (urlJobId) {
            if (!this.urlSelectedJobValid) {
                this.selectedJobId = null;
                return;
            }
            this.selectedJobId = urlJobId;
        } else if (['/index', '/taskRunning'].includes(window.location.pathname)) {
            const firstJob = this.jobsList[0];
            jobIdSyncService.setJobId(firstJob.job_id);
            this.selectedJobId = firstJob.job_id;
        } else {
            this.selectedJobId = null;
            return;
        }

        // 更新 selectedJobDetail 的快照数据
        if (this.selectedJobId) {
            const info = this.getJobListInfo(this.selectedJobId);
            if (info) {
                const wasNotAutoTerminated = !this.selectedJobDetail.isAutoTerminated;

                this.selectedJobDetail.job_id = info.job_id;
                this.selectedJobDetail.status = info.status;
                this.selectedJobDetail.stage_status = info.stage_status;
                this.selectedJobDetail.name = info.name;
                this.selectedJobDetail.iteration = info.iteration;
                this.selectedJobDetail.iterationSum = info.iterationSum;
                this.selectedJobDetail.sourceType = info.sourceType;
                this.selectedJobDetail.page = info.page;
                this.selectedJobDetail.cloudType = info.cloudType;
                this.selectedJobDetail.isAutoTerminated = info.isAutoTerminated;
                this.selectedJobDetail.sandboxExists = info.sandboxExists;
                this.selectedJobDetail.consumedCredit = info.consumedCredit;
                this.selectedJobDetail.refundedCredit = info.refundedCredit;

                if (
                    wasNotAutoTerminated
                    && info.isAutoTerminated
                    && !rootStore.userStore.isTaskTerminatedPrompted(info.job_id)
                    && !this.taskTerminatedModalVisible
                ) {
                    this.setTaskTerminatedModalVisible(true);
                }
            }
        }
    }

    // 设置选中的任务ID
    setSelectedJobId(id: string | null) {
        if (this.selectedJobId && this.selectedJobId !== id) {
            requestManager.cancel(`uploadAlgorithm_${this.selectedJobId}`);
        }
        this.selectedJobId = id;
    }

    setSelectedJobDetail(job: JobStore) {
        this.selectedJobDetail = job;
    }

    setTaskTerminatedModalVisible(visible: boolean) {
        this.taskTerminatedModalVisible = visible;
    }

    setCurrentJobDetail(job: JobStore) {
        this.currentJobDetail = job;
    }

    countTasksByStatus = (countInfo: TaskListNumberType) => {
        if (!isEmpty(countInfo)) {
            // 任务总个数来限制创建任务
            this.totalNumber = countInfo.all;
            // 初始化计数器
            this.countTasksByStatusNumber = {
                ALL: countInfo.all,
                DRAFT: countInfo.draft,
                RUNNING: countInfo.running,
                COMPLETED: countInfo.completed,
                PAUSED: countInfo.paused,
                CANCELLED: countInfo.cancelled
            };
        }
    };

}
